import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme, Drawer, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import FlowNavigation from '../FlowEditor/FlowNavigation';
import ProfileMenu from '../Auth/ProfileMenu';

interface AppLayoutProps {
  children: React.ReactNode;
}

const NAV_WIDTH = 280;
const COLLAPSED_WIDTH = 64;
const HEADER_HEIGHT = 64;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { workflowId } = useParams<{ workflowId: string }>();
  const [navigationOpen, setNavigationOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Don't show navigation on landing page
  const isLandingPage = location.pathname === '/';
  if (isLandingPage) {
    return <>{children}</>;
  }

  // Determine workflow title
  const getWorkflowTitle = () => {
    if (location.pathname.includes('/flow-editor/')) {
      if (workflowId === 'new') {
        return 'Creating New Workflow';
      }
      return workflowId || 'Workflow';
    }
    return '';
  };

  const workflowTitle = getWorkflowTitle();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        overflow: 'hidden',
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={navigationOpen}
          onClose={() => setNavigationOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: NAV_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          <FlowNavigation
            open={true}
            onToggle={() => setNavigationOpen(false)}
            width={NAV_WIDTH}
            collapsedWidth={COLLAPSED_WIDTH}
          />
        </Drawer>
      ) : (
        <Box
          sx={{
            width: navigationOpen ? NAV_WIDTH : COLLAPSED_WIDTH,
            flexShrink: 0,
            transition: 'width 0.3s',
            height: '100vh',
            zIndex: 1200,
          }}
        >
          <FlowNavigation
            open={navigationOpen}
            onToggle={() => setNavigationOpen(!navigationOpen)}
            width={NAV_WIDTH}
            collapsedWidth={COLLAPSED_WIDTH}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: '100vh',
          width: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header */}
        <Box 
          sx={{ 
            height: HEADER_HEIGHT,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            px: 3,
            py: 1,
            background: 'white',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            zIndex: 1000,
            position: 'sticky',
            top: 0,
            flexShrink: 0
          }}
        >
          {workflowTitle && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '1.1rem',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {workflowTitle}
            </Typography>
          )}
          <Box sx={{ 
            position: 'relative',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            ml: 'auto'
          }}>
          <ProfileMenu />
          </Box>
        </Box>
        
        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
        {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout; 